/**
 * The keyboard is derived from the exercise served — never from the child.
 *
 * SPEC §4.2 and invariant 8. Baya never meets an integral sign, and the reason
 * matters: not because a switch was flipped for her level, but because no 5ᵉ
 * exercise ever asks for one. There is no second configuration to keep in sync
 * with `exercise`, and no way for a mis-set profile to put a limit sign in front
 * of a twelve-year-old.
 *
 * The layout is built from what the exercise **already declares** — `answer.kind`,
 * the equivalence mode, the unit — plus the symbols its own canonical answer
 * uses. An exercise that never writes a square root offers no square root key.
 *
 * Two decisions come straight from the field session of 11 August 2026
 * (`prototype/resultats/ANALYSE.md`):
 *
 * * Baya wrote `3÷4` and never once reached for the fraction key. So `÷` stays —
 *   taking away what she spontaneously uses would be punishing her for being
 *   right — and the fraction key becomes the largest key on the board, labelled,
 *   in the position the thumb reaches first.
 * * Mehdi spent 28 seconds escaping an exponent. So every layout carries an
 *   explicit, permanently visible **sortir** key (`moveAfterParent`), and it is
 *   an action-coloured, double-width one. Guessing which arrow leaves a
 *   superscript is not a skill this project is trying to teach.
 */

import type { VirtualKeyboardLayout, VirtualKeyboardKeycap } from "mathlive";

export type AnswerKind = "symbolic" | "numeric" | "quantity" | "short_text" | "rubric";

/** What `exercise` declares about the expected answer (`exercise` §3). */
export type AnswerSpec = {
  kind: AnswerKind;
  equivalence?: string | null;
  unit?: string | null;
  significant_figures?: number | null;
  /** LaTeX of the canonical answer, when the tutor sends it for shaping keys. */
  canonical_latex?: string | null;
  /** Variables the statement uses, e.g. `["x", "n"]`. */
  variables?: string[] | null;
};

/**
 * The vocabulary of the board. A token absent from the set is a key that does
 * not exist — not a key that is disabled.
 */
export type KeyToken =
  | "digits"
  | "decimal"
  | "sign"
  | "arithmetic"
  | "divide"
  | "fraction"
  | "parentheses"
  | "equals"
  | "power"
  | "sqrt"
  | "variables"
  | "pi"
  | "unit"
  | "scientific"
  | "trigonometry"
  | "logarithm"
  | "integral"
  | "derivative"
  | "limit";

const ADVANCED: readonly KeyToken[] = ["trigonometry", "logarithm", "integral", "derivative", "limit"];

/** Which advanced notations the exercise's own answer actually contains. */
function tokensFromLatex(latex: string): KeyToken[] {
  const found: KeyToken[] = [];
  if (/\\int/.test(latex)) found.push("integral");
  if (/\\frac\s*\{\s*(d|\\mathrm\{d\})\s*\}/.test(latex) || /\\partial/.test(latex)) {
    found.push("derivative");
  }
  if (/\\lim/.test(latex)) found.push("limit");
  if (/\\(sin|cos|tan|arcsin|arccos|arctan)/.test(latex)) found.push("trigonometry");
  if (/\\(ln|log|exp)/.test(latex) || /e\^/.test(latex)) found.push("logarithm");
  if (/\\pi/.test(latex)) found.push("pi");
  if (/\\sqrt/.test(latex)) found.push("sqrt");
  if (/\^/.test(latex)) found.push("power");
  if (/\\frac/.test(latex)) found.push("fraction");
  if (/\\times\s*10/.test(latex)) found.push("scientific");
  return found;
}

/**
 * The token set for one exercise. Pure, and the subject of the invariant test:
 * no 5ᵉ answer specification may ever produce an advanced token.
 */
export function tokensFor(spec: AnswerSpec): Set<KeyToken> {
  const tokens = new Set<KeyToken>();
  if (spec.kind === "short_text" || spec.kind === "rubric") return tokens; // no math field at all

  tokens.add("digits");
  tokens.add("decimal");
  tokens.add("sign");

  if (spec.kind === "numeric") {
    // A number, and the ways a number is legitimately written. Nothing else:
    // an arithmetic key here would invite an unsimplified expression as an
    // answer, and then a "wrong" that is not wrong.
    if (spec.significant_figures) tokens.add("scientific");
  }

  if (spec.kind === "quantity") {
    tokens.add("scientific");
    if (spec.unit) tokens.add("unit");
  }

  if (spec.kind === "symbolic") {
    tokens.add("arithmetic");
    tokens.add("divide");
    tokens.add("fraction");
    tokens.add("parentheses");
    tokens.add("power");
  }

  const equivalence = spec.equivalence ?? "";
  if (/fraction/.test(equivalence)) {
    tokens.add("fraction");
    tokens.add("divide");
  }
  if (/equation|expanded|factored|expression/.test(equivalence)) {
    tokens.add("parentheses");
    tokens.add("equals");
  }

  if (spec.variables?.length) {
    tokens.add("variables");
    tokens.add("parentheses");
  }

  for (const token of tokensFromLatex(spec.canonical_latex ?? "")) tokens.add(token);

  return tokens;
}

/** Les seules touches d'un nombre écrit à la main : rien que MathLive apporte. */
const PLAIN_NUMBER: readonly KeyToken[] = ["digits", "decimal", "sign"];

/**
 * Cet exercice attend-il un nombre, et rien qu'un nombre ?
 *
 * **Alors MathLive ne sert à rien, et il nuit.** Signalé par Hedi le 28 août
 * 2026 en testant sur PC : « le clavier contient uniquement des nombres qui sont
 * désorganisés — pourquoi ne pas utiliser un clavier natif ? ». Il a raison sur
 * les deux points. Un pavé de chiffres dessiné à la main perd contre celui du
 * système : celui d'Android est celui que l'enfant connaît déjà, celui du PC est
 * le clavier physique, et les deux savent effacer, sélectionner et annuler sans
 * qu'on le réécrive. Les rangées de `keyboardFor` ne s'alignent d'ailleurs pas
 * quand la moitié droite est vide, faute d'opérateurs à y mettre.
 *
 * Reste ce que MathLive apporte vraiment — fraction, puissance, racine — et
 * personne ne le lui retire : ce test ne répond oui que si l'exercice ne déclare
 * aucune de ces notations. Le jugement est dérivé de `tokensFor`, la même source
 * que le clavier, pour que les deux ne puissent pas diverger.
 */
export function isPlainNumber(spec: AnswerSpec): boolean {
  const tokens = tokensFor(spec);
  if (tokens.size === 0) return false;
  for (const token of tokens) {
    if (!PLAIN_NUMBER.includes(token)) return false;
  }
  return true;
}

// --- Keycaps ----------------------------------------------------------------

type Keycap = Partial<VirtualKeyboardKeycap>;

const BLANK: Keycap = { class: "separator w5" };

/** Big, action-coloured, and always in the same place: escape from a nest. */
const EXIT: Keycap = {
  label: "sortir <span class='exit-glyph'>&#x21b3;</span>",
  tooltip: "sortir de la fraction ou de la puissance",
  command: ["performWithFeedback", "moveAfterParent"],
  class: "action exit-key",
  width: 2.0,
};

const BACKSPACE: Keycap = {
  label: "&#x232b;",
  tooltip: "effacer",
  command: ["performWithFeedback", "deleteBackward"],
  class: "action",
};

const LEFT: Keycap = {
  label: "&#x2190;",
  command: ["performWithFeedback", "moveToPreviousChar"],
  class: "action",
};

const RIGHT: Keycap = {
  label: "&#x2192;",
  command: ["performWithFeedback", "moveToNextChar"],
  class: "action",
};

/**
 * The fraction key, deliberately oversized.
 *
 * `#@` puts what precedes the caret into the numerator, so tapping it right
 * after a `3` gives `3` over an empty denominator — the gesture that matches
 * "trois quarts" said out loud.
 */
const FRACTION: Keycap = {
  latex: "\\frac{#@}{#?}",
  tooltip: "fraction",
  class: "small",
  width: 2.0,
};

function unitKey(unit: string): Keycap {
  return { latex: `\\,\\mathrm{${unit}}`, tooltip: `unité : ${unit}`, class: "small" };
}

function variableKeys(variables: string[]): Keycap[] {
  return variables.slice(0, 4).map((name) => ({ latex: name, class: "tex" }));
}

/**
 * Build the layout. One layer, always: a second layer is one more thing to
 * discover, and the field session says discovery is exactly what costs seconds.
 */
export function keyboardFor(spec: AnswerSpec): VirtualKeyboardLayout | null {
  const tokens = tokensFor(spec);
  if (tokens.size === 0) return null;

  const right = (...caps: Keycap[]): Keycap[] => [BLANK, ...caps];

  const row1: Keycap[] = ["7", "8", "9"].map(asKey);
  const row2: Keycap[] = ["4", "5", "6"].map(asKey);
  const row3: Keycap[] = ["1", "2", "3"].map(asKey);
  const row4: Keycap[] = [asKey("0"), { latex: ",", tooltip: "virgule" }];

  // Right-hand column: the operators this exercise can possibly need.
  const ops1: Keycap[] = [];
  const ops2: Keycap[] = [];
  const ops3: Keycap[] = [];

  if (tokens.has("divide")) ops1.push({ latex: "\\div" });
  if (tokens.has("fraction")) ops1.push(FRACTION);
  if (tokens.has("sqrt")) ops1.push({ latex: "\\sqrt{#?}" });

  if (tokens.has("arithmetic")) ops2.push({ latex: "\\times" });
  if (tokens.has("power")) {
    ops2.push({ latex: "#@^2", tooltip: "au carré" });
    ops2.push({ latex: "#@^{#?}", tooltip: "puissance", class: "small" });
  }

  if (tokens.has("sign")) ops3.push({ latex: "-" });
  if (tokens.has("arithmetic")) ops3.push({ latex: "+" });
  if (tokens.has("parentheses")) ops3.push(asKey("("), asKey(")"));
  if (tokens.has("equals")) ops3.push(asKey("="));

  const extras: Keycap[] = [];
  if (tokens.has("variables") && spec.variables) extras.push(...variableKeys(spec.variables));
  if (tokens.has("pi")) extras.push({ latex: "\\pi" });
  if (tokens.has("unit") && spec.unit) extras.push(unitKey(spec.unit));
  if (tokens.has("scientific")) extras.push({ latex: "\\times 10^{#?}", class: "small" });
  if (tokens.has("sqrt") && !ops1.some((cap) => cap.latex === "\\sqrt{#?}")) {
    extras.push({ latex: "\\sqrt{#?}" });
  }
  if (tokens.has("trigonometry")) {
    extras.push({ latex: "\\sin", class: "small" }, { latex: "\\cos", class: "small" });
    extras.push({ latex: "\\tan", class: "small" });
  }
  if (tokens.has("logarithm")) {
    extras.push({ latex: "\\ln(#?)", class: "small" }, { latex: "e^{#?}", class: "small" });
  }
  if (tokens.has("integral")) extras.push({ latex: "\\int_{#?}^{#?}#?", class: "small" });
  if (tokens.has("derivative")) {
    extras.push({ latex: "\\frac{\\mathrm{d}}{\\mathrm{d}#?}", class: "small" });
  }
  if (tokens.has("limit")) extras.push({ latex: "\\lim_{#?}", class: "small" });

  const rows: Keycap[][] = [
    [...row1, ...right(...ops1)],
    [...row2, ...right(...ops2)],
    [...row3, ...right(...ops3)],
    [...row4, BLANK, EXIT, BACKSPACE],
    [LEFT, RIGHT, BLANK, ...extras],
  ].filter((row) => row.length > 0);

  return {
    label: "clavier",
    tooltip: "clavier de l'exercice",
    displayEditToolbar: false,
    rows,
  };
}

function asKey(key: string): Keycap {
  return { key };
}

/** Reads the layout back as the set of LaTeX fragments it can ever insert. */
export function symbolsOffered(layout: VirtualKeyboardLayout | null): string[] {
  if (!layout || !("rows" in layout) || !layout.rows) return [];
  const symbols: string[] = [];
  for (const row of layout.rows) {
    for (const cap of row) {
      if (typeof cap === "string") symbols.push(cap);
      else if (cap.latex) symbols.push(cap.latex);
      else if (cap.key) symbols.push(cap.key);
    }
  }
  return symbols;
}

/** The notations no `collège` exercise may ever put on screen (SPEC §15). */
export const LYCEE_ONLY_PATTERNS: readonly RegExp[] = [
  /\\int/,
  /\\lim/,
  /\\partial/,
  /\\mathrm\{d\}/,
  /\\sin|\\cos|\\tan/,
  /\\ln|\\log/,
  /\\forall|\\exists/,
];

export function containsAdvancedNotation(layout: VirtualKeyboardLayout | null): boolean {
  return symbolsOffered(layout).some((symbol) =>
    LYCEE_ONLY_PATTERNS.some((pattern) => pattern.test(symbol)),
  );
}

export function isAdvanced(token: KeyToken): boolean {
  return ADVANCED.includes(token);
}
